import { makeInflectionCell } from "@/helpers/dictsys-inflection-scheme/inflection-scheme.helper";
import type {
  DictsysInflectionSchemeContent,
  DictsysInflectionSchemeGermanVerbIndicative,
  DictsysInflectionSchemeGermanVerbMood,
  DictsysInflectionSchemeGermanVerbPerfectTenses,
  DictsysInflectionSchemeGermanVerbPerson
} from "@/types/article-formats/dictsys";
import type { InflectionScheme, InflectionTable } from "@/types/inflection-scheme.type";

type IndicativeTense = keyof DictsysInflectionSchemeGermanVerbIndicative;
type PerfectTense = keyof DictsysInflectionSchemeGermanVerbPerfectTenses;

export type Tense = IndicativeTense | PerfectTense;

export type PersonKey = keyof Omit<
  DictsysInflectionSchemeGermanVerbPerson,
  "third_person_he_singular" | "third_person_she_singular"
>;

export interface LevelMap {
  beginner: string;
  intermediate: string;
  expert: string;
}

const personMap: Record<PersonKey, LevelMap> = {
  first_person_singular: {
    beginner: "ich (jeg)",
    intermediate: "ich",
    expert: "ich"
  },
  second_person_singular: {
    beginner: "du (du)",
    intermediate: "du",
    expert: "du"
  },
  third_person_it_singular: {
    beginner: "er/sie/es (han, hun, den/det)",
    intermediate: "er/sie/es",
    expert: "er/sie/es"
  },
  first_person_plural: {
    beginner: "wir (vi)",
    intermediate: "wir",
    expert: "wir"
  },
  second_person_plural: {
    beginner: "ihr (I)",
    intermediate: "ihr",
    expert: "ihr"
  },
  third_person_plural: {
    beginner: "sie/Sie (de/De)",
    intermediate: "sie/Sie",
    expert: "sie/Sie"
  }
};

function makeInflectionRow(inflections: DictsysInflectionSchemeContent[] | null) {
  let inflection = "-";
  let prefixes = "";

  // Merge prefixes and inflection (E.g. 3rd person singular er/sie/es has same inflection, but different prefixes)
  if (inflections !== null) {
    prefixes = inflections.map((cell) => cell.prefix).join("/");
    inflection = inflections[0].inflexion;
  }

  return makeInflectionCell(inflection, prefixes);
}

function makeInflectionTable(
  persons: DictsysInflectionSchemeGermanVerbPerson,
  level: keyof LevelMap
): InflectionTable {
  return {
    rows: [
      {
        cells: [
          makeInflectionCell(personMap["first_person_singular"][level]),
          makeInflectionRow(persons.first_person_singular)
        ]
      },
      {
        cells: [
          makeInflectionCell(personMap["second_person_singular"][level]),
          makeInflectionRow(persons.second_person_singular)
        ]
      },
      {
        cells: [
          makeInflectionCell(personMap["third_person_it_singular"][level]),
          makeInflectionRow(persons.third_person_it_singular)
        ]
      },
      {
        isDelimiter: true,
        cells: []
      },
      {
        cells: [
          makeInflectionCell(personMap["first_person_plural"][level]),
          makeInflectionRow(persons.first_person_plural)
        ]
      },
      {
        cells: [
          makeInflectionCell(personMap["second_person_plural"][level]),
          makeInflectionRow(persons.second_person_plural)
        ]
      },
      {
        cells: [
          makeInflectionCell(personMap["third_person_plural"][level]),
          makeInflectionRow(persons.third_person_plural)
        ]
      }
    ],
    columns: []
  };
}

export function makeInflectionSchemes(
  inflectionScheme: DictsysInflectionSchemeGermanVerbMood,
  level: keyof LevelMap
): InflectionScheme<Tense>[] {
  const iterableScheme = [];

  for (const [tense, persons] of Object.entries(inflectionScheme.indicative)) {
    iterableScheme.push({
      key: tense,
      table: makeInflectionTable(persons, level)
    });
  }

  // Some verbs don't have perfect tenses, so we need to check if it exists
  // Example: bergsteigen, wahrhaben
  if (inflectionScheme.perfect_tenses !== undefined) {
    for (const [tense, persons] of Object.entries(inflectionScheme.perfect_tenses)) {
      iterableScheme.push({
        key: tense,
        table: makeInflectionTable(persons, level)
      });
    }
  }

  return iterableScheme;
}
